import axios, { AxiosResponse } from 'axios';
import { auth } from '@/lib/firebase';
import type { DevelopmentMandateSelections } from '@/config/developmentMandate';
import type { EffectiveMandateResponse, ScriptProject } from '@/types/scriptProject';
import type { WorkflowScriptListItem } from '@/types/workflow';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const projectApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

projectApi.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
  }
  return config;
});

type ProjectsResponse = { status: string; projects: ScriptProject[] };
type ProjectResponse = { status: string; project: ScriptProject };
type ProjectUpdateResponse = ProjectResponse & { reassessed_scripts?: number };
type ScriptsResponse = { status: string; scripts: WorkflowScriptListItem[] };

export type ProjectUpdateResult = {
  project: ScriptProject;
  reassessed_scripts: number;
};

export async function listProjects(): Promise<ScriptProject[]> {
  const response: AxiosResponse<ProjectsResponse> = await projectApi.get('/api/projects');
  if (response.data.status !== 'success') {
    throw new Error('Failed to list projects');
  }
  return response.data.projects;
}

export async function createProject(params: {
  name: string;
  development_mandate?: DevelopmentMandateSelections;
}): Promise<ScriptProject> {
  const response: AxiosResponse<ProjectResponse> = await projectApi.post('/api/projects', params);
  if (response.data.status !== 'success') {
    throw new Error('Failed to create project');
  }
  return response.data.project;
}

export async function getProject(projectId: string): Promise<ScriptProject> {
  const response: AxiosResponse<ProjectResponse> = await projectApi.get(
    `/api/projects/${projectId}`,
  );
  if (response.data.status !== 'success') {
    throw new Error('Failed to get project');
  }
  return response.data.project;
}

export async function updateProject(
  projectId: string,
  params: { name?: string; development_mandate?: DevelopmentMandateSelections },
): Promise<ProjectUpdateResult> {
  const response: AxiosResponse<ProjectUpdateResponse> = await projectApi.patch(
    `/api/projects/${projectId}`,
    params,
  );
  if (response.data.status !== 'success') {
    throw new Error('Failed to update project');
  }
  return {
    project: response.data.project,
    reassessed_scripts: response.data.reassessed_scripts ?? 0,
  };
}

export async function listProjectScripts(projectId: string): Promise<WorkflowScriptListItem[]> {
  const response: AxiosResponse<ScriptsResponse> = await projectApi.get(
    `/api/projects/${projectId}/scripts`,
  );
  if (response.data.status !== 'success') {
    throw new Error('Failed to list project scripts');
  }
  return response.data.scripts;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export async function deleteProject(projectId: string): Promise<void> {
  try {
    const response = await projectApi.delete(`/api/projects/${projectId}`);
    if (response.data.status !== 'success') {
      throw new Error('Failed to delete mandate');
    }
  } catch (error: unknown) {
    throw new Error(apiErrorMessage(error, 'Failed to delete mandate'));
  }
}

export async function deleteScript(sessionId: string): Promise<void> {
  try {
    const response = await projectApi.delete(`/api/workflow/${sessionId}`);
    if (response.data.status !== 'success') {
      throw new Error('Failed to delete script');
    }
  } catch (error: unknown) {
    throw new Error(apiErrorMessage(error, 'Failed to delete script'));
  }
}

export async function getEffectiveMandate(projectId: string): Promise<EffectiveMandateResponse> {
  const response: AxiosResponse<EffectiveMandateResponse> = await projectApi.get(
    `/api/projects/${projectId}/mandate/effective`,
  );
  if (response.data.status !== 'success') {
    throw new Error('Failed to get effective mandate');
  }
  return response.data;
}
